'use client';

import {
  type Locale,
  SafeI18nProvider,
  messages as allMessages,
  defaultLocale,
  locales,
  useTranslations,
} from '@dealerportal/i18n/client';
import { captureException } from '@sentry/nextjs';
import { useEffect } from 'react';

/**
 * Default timezone fallback in case detection fails.
 */
const defaultTimeZone = 'America/New_York';

/**
 * Detects locale on the client side from URL path or browser language
 * This is a synchronous client-side alternative to server-side getLocale()
 */
function detectClientLocale(): Locale {
  // Try to read from URL path first (e.g., /en/, /pt/, /es/)
  if (typeof window !== 'undefined') {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    if (
      pathSegments.length > 0 &&
      locales.includes(pathSegments[0] as Locale)
    ) {
      return pathSegments[0] as Locale;
    }
  }

  // Fall back to browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.split('-')[0];
    if (locales.includes(browserLang as Locale)) {
      return browserLang as Locale;
    }
  }

  // Default locale
  return defaultLocale;
}

/**
 * Detects timezone on the client side
 * This is a synchronous client-side alternative to server-side getTimeZone()
 */
function detectClientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return defaultTimeZone;
  }
}

function GlobalErrorContent({
  error,
}: {
  error: Error & { digest?: string };
}) {
  const t = useTranslations('common');

  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>{t('globalErrorTitle')}</h1>
      <p>{t('globalErrorMessage')}</p>
    </div>
  );
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  // Synchronously detect locale and timezone on the client
  const locale = detectClientLocale();
  const messages = allMessages[locale];
  const timeZone = detectClientTimeZone();
  const now = new Date();

  return (
    <html lang={locale}>
      <body>
        <SafeI18nProvider
          locale={locale}
          messages={messages}
          allMessages={allMessages}
          timeZone={timeZone}
          now={now}
        >
          <GlobalErrorContent error={error} />
        </SafeI18nProvider>
      </body>
    </html>
  );
}
